// import react hooks
import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// import mantine UI components
import {
  Paper,
  Title,
  Group,
  Button,
  Box,
  LoadingOverlay,
  Alert,
} from '@mantine/core';
import { IconPlus, IconInfoCircle } from '@tabler/icons-react';

// import custom hooks
import usePartner from '../hooks/partners/usePartner.ts';
import useRefuelingContract from '../hooks/refueling-contracts/use-refueling-contract.ts';
import useCreateRefuelingContract from '../hooks/refueling-contracts/use-create-refueling-contract.ts';

// import interface, type
import type { CreateRefuelingContractRequest } from '../api/requests/refueling-contract-request.ts';
import type {
  MutateRefuelingContractFormValues,
  MutateRefuelingContractFormInitialValues,
} from '../components/ui/MutateRefuelingContractForm.tsx';

// import custom UI component
import MutateRefuelingContractForm from '../components/ui/MutateRefuelingContractForm.tsx';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';

export default function RefuelingContractNewPage() {
  // react hooks
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerId } = useParams();
  const isCloned: boolean = !!state?.cloneId;
  const cloneId: string = state?.cloneId;

  // api hooks
  const { partner, isLoading: isPartnerLoading } = usePartner(partnerId);
  const {
    refuelingContract: cloneContract,
    isLoading: isCloneContractLoading,
  } = useRefuelingContract(partnerId, cloneId, isCloned);
  const {
    mutate: createRefuelingContract,
    isPending: isCreateRefuelingContractPending,
  } = useCreateRefuelingContract();

  // UI hooks
  const [isClonedNoticeClosed, setClonedNoticeClosed] = useState(false);

  if (isPartnerLoading || isCloneContractLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // constants
  const lastPagePath: string = `/partners/${partnerId}`;
  const shouldDisplayClonedNotice: boolean = isCloned && !isClonedNoticeClosed;
  const initialFormValues: MutateRefuelingContractFormInitialValues =
    cloneId && cloneContract
      ? {
          name: '',
          startAt: null,
          endAt: null,
          disbursementFeeProcessingMethod:
            cloneContract.disbursementFeeProcessingMethod,
          gasStationNotificationEndpoint: '',
          gasBaseDiscount: cloneContract.gasBaseDiscount,
          dieselBaseDiscount: cloneContract.dieselBaseDiscount,
          receiptMode: cloneContract.receiptMode,
          gasStationAccountOwnerType: cloneContract.gasStationAccountOwnerType,
          gasStationAccountThreshold:
            cloneContract.gasStationAccountOwnerType !== 'JustLog'
              ? cloneContract.gasStationAccountThreshold
              : null,
          gasStationAccountAutoDepositAmount:
            cloneContract.gasStationAccountOwnerType !== 'JustLog'
              ? cloneContract.gasStationAccountAutoDepositAmount
              : null,
          existingAttachments: [],
        }
      : {
          name: '',
          startAt: null,
          endAt: null,
          disbursementFeeProcessingMethod: 'PRE_DEDUCTION',
          gasStationNotificationEndpoint: '',
          gasBaseDiscount: null,
          dieselBaseDiscount: null,
          receiptMode: 'ONSITE_PARTNER',
          gasStationAccountOwnerType: 'JustLog',
          gasStationAccountThreshold: null,
          gasStationAccountAutoDepositAmount: null,
          existingAttachments: [],
        };

  // callback functions
  function onSubmit(values: MutateRefuelingContractFormValues): void {
    if (isCreateRefuelingContractPending) return;

    const request: CreateRefuelingContractRequest = {
      partnerId: partnerId,
      name: values.name,
      startAt: values.startAt,
      endAt: values.endAt,
      disbursementFeeProcessingMethod: values.disbursementFeeProcessingMethod,
      gasStationNotificationEndpoint: values.gasStationNotificationEndpoint,
      gasBaseDiscount: values.gasBaseDiscount,
      dieselBaseDiscount: values.dieselBaseDiscount,
      receiptMode: values.receiptMode,
      gasStationAccountOwnerType: values.gasStationAccountOwnerType,
      gasStationAccountThreshold:
        values.gasStationAccountOwnerType !== 'JustLog'
          ? values.gasStationAccountThreshold
          : null,
      gasStationAccountAutoDepositAmount:
        values.gasStationAccountOwnerType !== 'JustLog'
          ? values.gasStationAccountAutoDepositAmount
          : null,
      file: values.newAttachments[0].file,
    };

    createRefuelingContract(request, {
      onSuccess: (id) => {
        // 轉導至 editPage
        navigate(`/partners/${partnerId}/refueling-contracts/${id}/edit`, {
          state: { created: true },
        });
      },
    });
  }

  return (
    <>
      <PreviousPageButton href={lastPagePath} content={'返回業者詳情頁'} />
      {shouldDisplayClonedNotice && (
        <Alert
          variant="light"
          color="green"
          mb={20}
          icon={<IconInfoCircle />}
          withCloseButton
          onClose={() => setClonedNoticeClosed(true)}
          title={`成功複製「${cloneContract.name}」合約`}
        >
          僅複製「手續費扣除方式」、「分潤設定」、「發票設定」、「現銷金設定」等欄位，其餘欄位仍需手動填寫
        </Alert>
      )}

      <Paper radius="lg" p="xl" shadow="xs" mb={70}>
        <Title order={3}>{`${partner.name} | 加油站合約`}</Title>
        <MutateRefuelingContractForm
          initialValues={initialFormValues}
          onSubmit={onSubmit}
        />
      </Paper>

      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            type="submit"
            form="mutateRefuelingContractForm"
            loading={isCreateRefuelingContractPending}
            fw={400}
            leftSection={<IconPlus />}
          >
            新增
          </Button>
        </Group>
      </Box>
    </>
  );
}
